import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { SaveHeightDirective } from '@modules/directives/save-height.directive';
import { TablePageDirective } from '@modules/directives/table-page.directive';
import { FChipsComponent } from '@modules/filter/components/chips/f-chips/f-chips.component';
import { spinner } from '@modules/spinner/spinner';
import { CompanyCard } from '@type/company.type';
import { FilterBean, FilterQ } from '@type/filter.type';
import { AdminLogs } from '@type/log.type';
import { PostRequest } from '@type/request.type';
import { map, Observable, tap } from 'rxjs';
import { splitSearchWords } from '@utils/search';
import { CompaniesService } from '../companies/services/companies.service';
import { CustomDateFormatPipe } from '../../modules/pipes/custom-date-format.pipe';
import { LogHeaderComponent } from './components/log-header/log-header.component';
import { LogService } from './services/log/log.service';

const DISPLAYED_COLUMNS = [
  'created_at',
  'user',
  'action',
  'loggable',
  'field_identifier',
  'field_value',
] as Array<keyof AdminLogs>;

@Component({
  selector: 'app-log-page',
  imports: [
    MatTableModule,
    MatIconModule,
    MatPaginatorModule,
    CustomDateFormatPipe,
    MatSortModule,
    SaveHeightDirective,
    LogHeaderComponent,
    FChipsComponent,
  ],
  templateUrl: './log-page.component.html',
  styleUrl: './log-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LogPageComponent extends TablePageDirective<AdminLogs> {
  private readonly logService = inject(LogService);
  private readonly companiesService = inject(CompaniesService);

  displayedColumns = signal(DISPLAYED_COLUMNS);

  activeTab = signal(0);
  selectedCompanyId = signal<number | null>(null);
  companiesList = signal<CompanyCard[]>([]);

  tableHeaderList: Record<string, string> = {
    created_at: 'Created At',
    user: 'User',
    action: 'Action',
    loggable: 'Type',
    field_identifier: 'Field Identifier',
    field_value: 'Field Value',
  };

  constructor() {
    super();
    this.companiesService.getCompaniesList()
      .pipe(takeUntilDestroyed())
      .subscribe(({ companies }) => {
        this.companiesList.set(companies);
        if (companies.length) this.selectedCompanyId.set(companies[0].id);
      });
  }

  switchTab(index: number) {
    this.activeTab.set(index);
    this.pagination.update(p => ({ ...p, pageIndex: 0 }));
  }

  selectCompany(id: number) {
    this.selectedCompanyId.set(id);
    this.pagination.update(p => ({ ...p, pageIndex: 0 }));
  }

  @spinner({ selectorById: 'spinner-container' })
  protected makeRequest(request: PostRequest): Observable<AdminLogs[]> {
    const companyId = this.selectedCompanyId();

    if (this.activeTab() === 0 && companyId) {
      return this.logService.getCompanyLogs(companyId, request).pipe(
        tap(({ length }) => this.length.set(length)),
        map(({ data }) => data),
      );
    }

    return this.logService.getLogs(request).pipe(
      tap(({ length }) => this.length.set(length)),
      map(({ data }) => data),
    );
  }

  protected override buildQuery(): Record<string, unknown> {
    void this.activeTab();
    void this.selectedCompanyId();

    const filter = this.filterService.filterForRequest() as FilterQ<FilterBean> | undefined;
    const to = filter?.q?.['created_at']?.to;

    return {
      created_at_gteq: filter?.q?.['created_at']?.from,
      created_at_lteq: to ? new Date(new Date(to).setUTCHours(23, 59, 59, 0)).toISOString() : undefined,
      user_i_cont_all: splitSearchWords(filter?.q?.['user']?.value as string | undefined),
      user_id_blank: filter?.q?.['user']?.is_null === true ? true : undefined,
      loggable_in: filter?.q?.['super_admin_logs_loggables']?.value,
      loggable_null: filter?.q?.['super_admin_logs_loggables']?.is_null,
      action_in: filter?.q?.['super_admin_logs_action']?.value,
      action_null: filter?.q?.['super_admin_logs_action']?.is_null,
      field_identifier_in: filter?.q?.['super_admin_logs_field_identifiers']?.value,
      field_identifier_null: filter?.q?.['super_admin_logs_field_identifiers']?.is_null,
      field_value_cont: filter?.q?.['field_value']?.value,
      field_value_null: filter?.q?.['field_value']?.is_null,
    };
  }
}
